import React, { useRef, useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'

import { Box, Typography, Input, Button, Alert } from '@mui/material'

import { editCategory, createCategory } from '../../apollo'
import ConfigurableValues from '../../config/constants'
import useGlobalStyles from '../../utils/globalStyles'
import useStyles from '../styles'
import FormCardHeader from '../FormCardHeader'

const CREATE_CATEGORY = gql`
  ${createCategory}
`
const EDIT_CATEGORY = gql`
  ${editCategory}
`

// Quick-pick emojis shown next to the category name in the customer app
const EMOJI_SUGGESTIONS = [
  '🥐',
  '🍞',
  '🥪',
  '🍔',
  '🍕',
  '🍗',
  '🥩',
  '🐟',
  '🍤',
  '🍚',
  '🍜',
  '🥗',
  '🍰',
  '🍪',
  '🍦',
  '☕',
  '🥤',
  '🍺',
  '🍷',
  '🧊',
  '🧴',
  '🧺',
  '🛒',
  '🌶️'
]

// Keeps a single emoji (some emojis are several code points, e.g. 🌶️)
const takeFirstEmoji = value => {
  const trimmed = (value || '').trim()
  if (!trimmed) return ''
  const graphemes = Array.from(trimmed)
  // Grab the first code point plus its modifiers/joiners
  let result = graphemes[0]
  for (let i = 1; i < graphemes.length; i++) {
    const char = graphemes[i]
    const code = char.codePointAt(0)
    const isModifier =
      code === 0xfe0f || // variation selector
      code === 0x200d || // zero width joiner
      (code >= 0x1f3fb && code <= 0x1f3ff) || // skin tones
      (code >= 0x20e3 && code <= 0x20e3)
    const previousCode = graphemes[i - 1].codePointAt(0)
    if (isModifier || previousCode === 0x200d) {
      result += char
    } else {
      break
    }
  }
  return result
}

function Category(props) {
  const mutation = props.category ? EDIT_CATEGORY : CREATE_CATEGORY
  const { CLOUDINARY_UPLOAD_URL, CLOUDINARY_FOOD } = ConfigurableValues()
  const fileInputRef = useRef()
  const [imageLoading, setImageLoading] = useState(false)
  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [category, setCategory] = useState(
    props.category ? props.category.title : ''
  )
  const [emoji, setEmoji] = useState(
    props.category ? props.category.emoji || '' : ''
  )
  // Used by stores whose page layout is the category grid
  const [image, setImage] = useState(
    props.category ? props.category.image || '' : ''
  )
  const restaurantId = localStorage.getItem('restaurantId')
  const onCompleted = data => {
    const message = props.category
      ? t('CategoryUpdatedSuccessfully')
      : t('CategoryAddedSuccessfully')
    successSetter(message)
    mainErrorSetter('')
    setCategory('')
    if (!props.category) {
      setEmoji('')
      removeImage()
    }
    setTimeout(hideAlert, 3000)
    // Editing happens in a modal: close it once the change is saved
    if (props.category && props.onClose) {
      setTimeout(() => props.onClose(), 1200)
    }
  }

  // Only one image per category: picking a new file replaces whatever was set
  // before (uploaded file or pasted url)
  const onSelectImage = event => {
    const picked = Array.from(event.target.files || []).find(file =>
      file.name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
    )
    // Let the same file be picked again after a remove
    event.target.value = ''
    if (!picked) return

    // Roomy enough for the full width artwork the home screen tiles now show
    if (picked.size > 5 * 1024 * 1024) {
      mainErrorSetter('File size should be less than or equal to 5MB')
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      mainErrorSetter('')
      setImage(reader.result)
    }
    reader.readAsDataURL(picked)
  }

  const removeImage = () => {
    setImage('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Base64 previews get pushed to cloudinary, plain urls are kept as they are
  const uploadImageToCloudinary = async () => {
    if (!image) return ''
    if (!image.startsWith('data:')) return image.trim()

    setImageLoading(true)
    try {
      const result = await fetch(`${CLOUDINARY_UPLOAD_URL}/image/upload`, {
        body: JSON.stringify({ file: image, upload_preset: CLOUDINARY_FOOD }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST'
      })
      const imageData = await result.json()
      if (!imageData.secure_url) throw new Error('No url returned')
      return imageData.secure_url
    } catch (e) {
      console.error('Category image upload error => ', e)
      mainErrorSetter('Failed to upload image to Cloudinary.')
      return null
    } finally {
      setImageLoading(false)
    }
  }
  const onError = error => {
    const message = `${t('ActionFailedTryAgain')} ${error}`
    successSetter('')
    mainErrorSetter(message)
    setTimeout(hideAlert, 3000)
  }
  const [mutate, { loading }] = useMutation(mutation, { onError, onCompleted })
  const hideAlert = () => {
    mainErrorSetter('')
    successSetter('')
  }
  const { t } = props
  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  return (
    <Box container className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        title={props.category ? t('Edit Category') : t('Add Category')}
        onClose={props.onClose}
      />
      <Box className={classes.form}>
        <form>
          <Box>
            <Typography className={classes.labelText}>{t('Name')}</Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-category"
              name="input-category"
              placeholder={t('PHCategory')}
              type="text"
              defaultValue={category}
              onChange={e => {
                setCategory(e.target.value)
              }}
              disableUnderline
              className={globalClasses.input}
            />
          </Box>
          <Box mt={2}>
            <Typography className={classes.labelText}>
              {t('Emoji')} ({t('Optional')})
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-category-emoji"
              name="input-category-emoji"
              placeholder={t('PHCategoryEmoji')}
              type="text"
              value={emoji}
              onChange={e => {
                setEmoji(takeFirstEmoji(e.target.value))
              }}
              disableUnderline
              className={globalClasses.input}
            />
            <Box
              mt={1}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                alignItems: 'center'
              }}>
              {EMOJI_SUGGESTIONS.map(suggestion => (
                <Button
                  key={suggestion}
                  type="button"
                  onClick={() =>
                    setEmoji(current =>
                      current === suggestion ? '' : suggestion
                    )
                  }
                  style={{
                    minWidth: 36,
                    height: 36,
                    padding: 0,
                    fontSize: 18,
                    lineHeight: '36px',
                    borderRadius: 8,
                    border:
                      emoji === suggestion
                        ? '2px solid #F6985D'
                        : '1px solid #E0E0E0',
                    backgroundColor:
                      emoji === suggestion ? '#FFF3E9' : 'transparent'
                  }}>
                  {suggestion}
                </Button>
              ))}
            </Box>
            <Typography
              className={classes.labelText}
              style={{ marginTop: 8, opacity: 0.7 }}>
              {t('Preview')}:{' '}
              {emoji
                ? `${category || t('Name')} ${emoji}`
                : category || t('Name')}
            </Typography>
          </Box>
          <Box mt={2}>
            <Typography className={classes.labelText}>
              {t('ImageUrl')} ({t('Optional')})
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-category-image"
              name="input-category-image"
              placeholder={t('PHShopTypeImage')}
              type="text"
              value={image.startsWith('data:') ? '' : image}
              onChange={e => {
                setImage(e.target.value)
              }}
              disabled={image.startsWith('data:')}
              disableUnderline
              className={globalClasses.input}
            />
            <Typography
              className={classes.labelText}
              style={{ opacity: 0.7, fontSize: 13 }}>
              {t('CategoryImageHelp')}
            </Typography>
            <Box
              mt={1}
              style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {image ? (
                <img
                  key={image}
                  className={classes.image}
                  alt={category}
                  src={image}
                  onError={event => {
                    event.target.style.visibility = 'hidden'
                  }}
                />
              ) : null}
              <Box>
                <label
                  htmlFor={
                    props.category
                      ? `edit-category-image-${props.category._id}`
                      : 'add-category-image'
                  }
                  className={classes.fileUpload}>
                  {t('UploadAnImage')}
                </label>
                <input
                  ref={fileInputRef}
                  className={classes.file}
                  id={
                    props.category
                      ? `edit-category-image-${props.category._id}`
                      : 'add-category-image'
                  }
                  type="file"
                  accept="image/*"
                  onChange={onSelectImage}
                />
                {image ? (
                  <Button
                    type="button"
                    onClick={removeImage}
                    style={{
                      marginLeft: 8,
                      fontSize: 12,
                      textTransform: 'none'
                    }}>
                    {t('Remove')}
                  </Button>
                ) : null}
              </Box>
            </Box>
          </Box>
          {loading || imageLoading ? t('Loading') : null}
          <Box mt={2}>
            <Button
              className={globalClasses.button}
              disabled={loading || imageLoading}
              onClick={async e => {
                e.preventDefault()
                if (!loading && !imageLoading) {
                  const uploadedImage = await uploadImageToCloudinary()
                  // Upload failed: keep the form as is so nothing is lost
                  if (uploadedImage === null) return
                  mutate({
                    variables: {
                      category: {
                        _id: props.category ? props.category._id : '',
                        title: category,
                        emoji,
                        // Replaces the previous image, or clears it when removed
                        image: uploadedImage,
                        restaurant: restaurantId
                      }
                    }
                  })
                }
              }}>
              {t('Save')}
            </Button>
          </Box>
          <Box mt={2}>
            {success && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {success}
              </Alert>
            )}
            {mainError && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {mainError}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(Category)
